import { FileText, Loader2, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import { ForeignNationalsAPI } from "@/api/foreignNationals";
import type { ForeignNational } from "@/api/types/foreignNational";
import type { AutofillProposals } from "@/api/types/foreignNationalRecords";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import {
  buildApplyRequest,
  buildReview,
  countActionable,
  defaultSelection,
  missingDates,
  type ReviewField,
  type ReviewGroup,
  type ReviewRow
} from "./autofillModel";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  client: ForeignNational;
  /** Called after the accepted values were stored. */
  onApplied: () => Promise<void> | void;
};

const STATE_TEXT = {
  match: "Matches the profile",
  update: "Updates the passport on file"
} as const;

const FieldItem: FC<{
  item: ReviewField;
  checked: boolean;
  onToggle: (on: boolean) => void;
}> = ({ item, checked, onToggle }) => {
  const match = item.state === "match";
  return (
    <label
      htmlFor={item.id}
      className={cn(
        "flex items-start gap-2.5 px-3 py-2",
        match ? "opacity-60" : "cursor-pointer hover:bg-fill-1"
      )}
      data-testid={`autofill-field-${item.field}`}
      data-state={item.state}
    >
      <Checkbox
        id={item.id}
        className="mt-0.5"
        checked={match ? false : checked}
        disabled={match}
        onCheckedChange={(v) => onToggle(v === true)}
        aria-label={`Use ${item.label}`}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline justify-between gap-3">
          <span className="text-xs text-muted-foreground">{item.label}</span>
          {item.state === "conflict" ? (
            <span className="text-xs font-medium text-warn">
              {item.note ?? "Differs from the profile"}
            </span>
          ) : match ? (
            <span className="text-xs text-muted-foreground">
              {STATE_TEXT.match}
            </span>
          ) : null}
        </span>
        <span className="text-base font-medium">{item.display}</span>
        {item.state === "conflict" && item.current ? (
          <span className="text-xs text-muted-foreground">
            On file: <span className="line-through">{item.current}</span>
          </span>
        ) : null}
      </span>
    </label>
  );
};

const RowItem: FC<{
  row: ReviewRow;
  checked: boolean;
  date: string;
  onToggle: (on: boolean) => void;
  onDate: (value: string) => void;
}> = ({ row, checked, date, onToggle, onDate }) => {
  const match = row.state === "match";
  return (
    <div
      className={cn(
        "flex items-start gap-2.5 px-3 py-2",
        match && "opacity-60"
      )}
      data-testid={`autofill-row-${row.kind}`}
      data-state={row.state}
    >
      <Checkbox
        className="mt-0.5"
        checked={match ? false : checked}
        disabled={match}
        onCheckedChange={(v) => onToggle(v === true)}
        aria-label={`Add ${row.title}`}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline justify-between gap-3">
          <span className="text-base font-medium">{row.title}</span>
          <span className="text-xs text-muted-foreground">
            {match
              ? "Already in the history"
              : row.state === "update"
              ? STATE_TEXT.update
              : "Adds a history row"}
          </span>
        </span>
        {row.detail ? (
          <span className="text-xs text-muted-foreground">{row.detail}</span>
        ) : null}
        {row.needs && !match ? (
          <span className="mt-1 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {row.needs.label}
            </span>
            <Input
              type="date"
              value={date}
              onChange={(e) => onDate(e.target.value)}
              className="h-7 w-40 text-xs"
              aria-label={row.needs.label}
            />
          </span>
        ) : null}
      </div>
    </div>
  );
};

const GroupCard: FC<{
  group: ReviewGroup;
  selected: ReadonlySet<string>;
  dates: Readonly<Record<string, string>>;
  toggle: (id: string, on: boolean) => void;
  setDate: (id: string, value: string) => void;
}> = ({ group, selected, dates, toggle, setDate }) => (
  <section className="overflow-hidden rounded-md border border-border">
    <header className="flex items-center gap-2 border-b border-border bg-fill-1 px-3 py-2">
      <FileText className="size-3.5 text-muted-foreground" />
      <span className="text-base font-medium">{group.label}</span>
      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
        {[group.title, group.caseLabel].filter(Boolean).join(" · ")}
      </span>
    </header>
    <div className="divide-y divide-border/60">
      {group.fields.map((f) => (
        <FieldItem
          key={f.id}
          item={f}
          checked={selected.has(f.id)}
          onToggle={(on) => toggle(f.id, on)}
        />
      ))}
      {group.rows.map((r) => (
        <RowItem
          key={r.id}
          row={r}
          checked={selected.has(r.id)}
          date={dates[r.id] ?? ""}
          onToggle={(on) => toggle(r.id, on)}
          onDate={(v) => setDate(r.id, v)}
        />
      ))}
    </div>
  </section>
);

/** Review what the case documents say before any of it lands on the profile. */
export const AutofillSheet: FC<Props> = ({
  open,
  onOpenChange,
  clientId,
  client,
  onApplied
}) => {
  const [proposals, setProposals] = useState<AutofillProposals | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dates, setDates] = useState<Record<string, string>>({});
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setProposals(null);
    setError(null);
    setDates({});
    ForeignNationalsAPI.getAutofill(clientId)
      .then((p) => live && setProposals(p))
      .catch(
        (e: unknown) =>
          live &&
          setError(
            e instanceof Error ? e.message : "Couldn't read the documents."
          )
      );
    return () => {
      live = false;
    };
  }, [open, clientId]);

  const groups = useMemo(
    () => (proposals ? buildReview(client, proposals) : []),
    [client, proposals]
  );
  useEffect(() => setSelected(defaultSelection(groups)), [groups]);

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const setDate = (id: string, value: string) => {
    setDates((prev) => ({ ...prev, [id]: value }));
    if (value) toggle(id, true);
  };

  const request = buildApplyRequest(groups, selected, dates);
  const waiting = missingDates(groups, selected, dates);
  const count = request.fields.length + request.history.length;
  const actionable = countActionable(groups);

  const apply = async () => {
    setApplying(true);
    try {
      await ForeignNationalsAPI.applyAutofill(clientId, request);
      toast.success(
        `Filled ${count} item${count === 1 ? "" : "s"} from the documents.`
      );
      await onApplied();
      onOpenChange(false);
    } catch (e) {
      toast.error("Couldn't apply the autofill.", {
        description: e instanceof Error ? e.message : undefined
      });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-130"
        data-testid="autofill-sheet"
      >
        <SheetHeader className="border-b border-border">
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="size-4" /> Autofill from documents
          </SheetTitle>
          <SheetDescription>
            {proposals
              ? `Read from the passport, I-94 and approval notices on ${
                  proposals.cases_checked
                } case${
                  proposals.cases_checked === 1 ? "" : "s"
                }. Values that differ from the profile aren't selected.`
              : "Reading the passport, I-94 and approval notices on this client's cases…"}
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : !proposals ? (
            Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-36 w-full rounded-md" />
            ))
          ) : groups.length === 0 ? (
            <div
              className="flex flex-col gap-1 rounded-md border border-dashed border-border p-4 text-sm"
              data-testid="autofill-empty"
            >
              <span className="font-medium">Nothing to fill yet</span>
              <span className="text-muted-foreground">
                {proposals.cases_checked
                  ? "Upload the passport, I-94 or an approval notice to a case; once they're read, their details show up here."
                  : "This client has no cases yet. Documents are read once they're in a case."}
              </span>
            </div>
          ) : (
            <>
              {actionable === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Everything the documents say already matches the profile.
                </p>
              ) : null}
              {groups.map((g) => (
                <GroupCard
                  key={g.key}
                  group={g}
                  selected={selected}
                  dates={dates}
                  toggle={toggle}
                  setDate={setDate}
                />
              ))}
            </>
          )}
        </div>
        <SheetFooter className="flex-row items-center justify-between border-t border-border">
          <span className="text-xs text-muted-foreground">
            {waiting.length
              ? `Add the ${waiting[0].needs?.label.toLowerCase()} for ${
                  waiting[0].title
                }`
              : count
              ? `${count} selected`
              : "Nothing selected"}
          </span>
          <span className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!count || applying || waiting.length > 0}
              onClick={() => void apply()}
              data-testid="autofill-apply"
            >
              {applying ? <Loader2 className="animate-spin" /> : null}
              Apply {count || ""}
            </Button>
          </span>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
